import React, { FunctionComponent, useEffect, useState } from 'react';
import { Fade, Typography } from '@mui/material';

import { ILessonHistory } from '../../../../../../../../../../store/V2/LessonHistorySlice';
import { useAppSelector } from '../../../../../../../../../../hooks';
import { getAuthState } from '../../../../../../../../../../store/AuthSlice';
import { checkUserPermission } from '../../../../../../../../../../helper';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../../../../store/V2/RolesSlice';
import TabBar, {
  ITabItem,
} from '../../../../../../../../../../components/V2/Tabs/type2/TabBar';
import AdjustmentsTab from './AdjustmentsTab';
import ClassExtensionTab from './ClassExtensionTab';
import TutorPenaltyTab from './TutorPenaltyTab';
import TrialConversionTab from './TrialConversionTab';
import { LESSON_TYPES } from '../../../../../../../../../../store/V2/SectionSlice';

interface PaymentAdjustmentsTabProps {
  lessonHistory: ILessonHistory;
  refreshListEnabled?: boolean;
  refreshList?: () => void;
}

export type TState = {
  selectedTab: ITabItem | null;
  tabs: ITabItem[];
};

const defaultTabOptions: ITabItem[] = [
  {
    index: 0,
    key: 'adjustments',
    title: 'Adjustments',
  },
  {
    index: 1,
    key: 'classExtension',
    title: 'Class Extension',
  },
  {
    index: 2,
    key: 'trialConversion',
    title: 'Conversion',
  },
  {
    index: 3,
    key: 'tutorPenalty',
    title: 'Penalties',
  },
];

const initialState: TState = {
  selectedTab: null,
  tabs: [],
};

const PaymentAdjustmentsTab: FunctionComponent<PaymentAdjustmentsTabProps> = ({
  lessonHistory,
  refreshListEnabled = false,
  refreshList,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState<TState>({ ...initialState });

  const authUser = authState.user;

  useEffect(() => {
    setState((prevState) => {
      const newState = { ...prevState };

      if (authUser) {
        const newTabs = [...defaultTabOptions].filter((option) => {
          if (option.key === 'adjustments') {
            return checkUserPermission(authUser, {
              adminLessonHistoryCompletedTab: [
                USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_ADJUSTMENTS,
              ],
            });
          }

          if (option.key === 'classExtension') {
            return checkUserPermission(authUser, {
              adminLessonHistoryCompletedTab: [
                USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_CLASS_EXTENSION,
              ],
            });
          }

          if (option.key === 'trialConversion') {
            return (
              checkUserPermission(authUser, {
                adminLessonHistoryCompletedTab: [
                  USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_TRIAL_CONVERSION,
                ],
              }) && lessonHistory.lessonType === LESSON_TYPES.TRIAL
            );
          }

          if (option.key === 'tutorPenalty') {
            return checkUserPermission(authUser, {
              adminLessonHistoryCompletedTab: [
                USER_PERMISSION_INDIVIDUALS.ADMIN_LESSON_HISTORY_COMPLETED_TAB_PAYMENT_ADJUSTMENTS_TUTOR_PENALTY,
              ],
            });
          }

          return false;
        });

        newState.tabs = [...newTabs].map((item, index) => ({
          ...item,
          index,
        }));

        if (newTabs.length) {
          // eslint-disable-next-line prefer-destructuring
          newState.selectedTab = newTabs[0];
        }
      }

      return newState;
    });
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const renderTabPanel = () => {
    let tabPanel: JSX.Element | null = null;

    if (state.selectedTab) {
      switch (state.selectedTab.key) {
        case 'adjustments':
          tabPanel = (
            <AdjustmentsTab
              lessonHistory={lessonHistory}
              refreshListEnabled={refreshListEnabled}
              refreshList={refreshList}
            />
          );
          break;

        case 'classExtension':
          tabPanel = (
            <ClassExtensionTab
              lessonHistory={lessonHistory}
              refreshListEnabled={refreshListEnabled}
              refreshList={refreshList}
            />
          );
          break;

        case 'trialConversion':
          tabPanel = (
            <TrialConversionTab
              lessonHistory={lessonHistory}
              refreshListEnabled={refreshListEnabled}
              refreshList={refreshList}
            />
          );
          break;

        case 'tutorPenalty':
          tabPanel = (
            <TutorPenaltyTab
              lessonHistory={lessonHistory}
              refreshListEnabled={refreshListEnabled}
              refreshList={refreshList}
            />
          );
          break;
        default:
          tabPanel = null;
          break;
      }
    }

    return tabPanel;
  };

  return (
    <Fade in>
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Typography fontWeight={600} fontSize="16px">
            Payment Adjustments
          </Typography>
        </div>
        {state.tabs.length ? (
          <>
            <div>
              <TabBar
                selectedTab={state.selectedTab || undefined}
                tabs={state.tabs}
                onChange={(e, value) => {
                  console.log('tab value', value);
                  updateStateAttributes({ selectedTab: state.tabs[value] });
                }}
              />
            </div>
            <div
              style={{
                borderBottom: '2px solid #1E293B14',
                width: '100%',
                height: '1px',
                marginTop: '-3px',
              }}
            />
            <div style={{ minHeight: '16px' }} />
            <div>{renderTabPanel()}</div>
          </>
        ) : null}
      </div>
    </Fade>
  );
};

export default PaymentAdjustmentsTab;
